import { useVirtualizer } from "@tanstack/react-virtual";
import { AlertCircle, CheckIcon, ChevronDown, Loader2, X } from "lucide-react";
import { useMemo, useState, useRef } from "react";
import { useSpinDelay } from "spin-delay";
import { PopoverTriggerButton } from "../PopoverTriggerButton";
import { type ComboboxProps, type ComboboxOption, type SingleProps, type MultiProps } from "./types";
import { usePortalContainer } from "@/components/PortalContainer";
import { Button } from "@/components/ui/button";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from "@/components/ui/command";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils/common";
import { getErrorMessage } from "@/lib/utils/errors";
import { getNextTabbable } from "@/lib/utils/focus";
import { composeRefs } from "@/lib/utils/refs";

export function Combobox<T extends string>({
  className,
  options,
  virtualisationThreshold = 100,
  inputRef,
  ref,
  ...props
}: ComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [initialSearch, setInitialSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const shouldVirtualize = options.length > virtualisationThreshold;
  // Accept both `inputRef` and `ref`, so spreading a React Hook Form `field` wires up focus-on-error
  const triggerRef = useMemo(() => composeRefs(inputRef, ref), [inputRef, ref]);

  const handleOpenChange = (nextOpen: boolean) => {
    setInitialSearch("");
    setOpen(nextOpen);
  };

  const handleOpenAutoFocus = (event: Event): void => {
    // Radix's default autofocus selects the character typed to open the popover.
    event.preventDefault();
    const input = searchInputRef.current;
    input?.focus();
    input?.setSelectionRange(input.value.length, input.value.length);
  };

  // Type-to-open: typing a character on the focused trigger opens the list with that character as the search
  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (open) return;

    const isPrintableCharacter = e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey && !e.altKey;
    if (!isPrintableCharacter && e.key !== "ArrowDown") return;

    e.preventDefault();
    setInitialSearch(isPrintableCharacter ? e.key : "");
    setOpen(true);
  };

  return (
    <ComboboxRoot
      {...props}
      inputRef={triggerRef}
      className={className}
      options={options}
      open={open}
      setOpen={handleOpenChange}
      onTriggerKeyDown={handleTriggerKeyDown}
      onOpenAutoFocus={handleOpenAutoFocus}
    >
      {shouldVirtualize ? (
        <VirtualisedOptionsList
          {...props}
          options={options}
          setOpen={handleOpenChange}
          initialSearch={initialSearch}
          searchInputRef={searchInputRef}
        />
      ) : (
        <OptionsList
          {...props}
          options={options}
          setOpen={handleOpenChange}
          initialSearch={initialSearch}
          searchInputRef={searchInputRef}
        />
      )}
    </ComboboxRoot>
  );
}

type OptionsListProps<T extends string> = {
  options: ComboboxOption<T>[];
  emptyText?: string;
  handleClear?: () => void;
  setOpen: (open: boolean) => void;
  initialSearch: string;
  searchInputRef: React.Ref<HTMLInputElement>;
} & (SingleProps<T> | MultiProps<T>);

function OptionsList<T extends string>(props: OptionsListProps<T>) {
  const [searchQuery, setSearchQuery] = useState(props.initialSearch);
  const filteredOptions = props.options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleSelect = (value: T) => {
    if (props.type === "multi") {
      const newSelected = props.selected.includes(value)
        ? props.selected.filter((item) => item !== value)
        : [...props.selected, value];
      props.onChange(newSelected);
    } else {
      props.onChange(props.selected === value ? null : value);
      props.setOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (props.type === "multi") {
      const filteredValues = filteredOptions.map((opt) => opt.value);
      const newSelected = [...new Set([...props.selected, ...filteredValues])];
      props.onChange(newSelected);
    }
  };

  const isSelected = (value: T) => (props.type === "multi" ? props.selected.includes(value) : props.selected === value);

  const showSelectAllButton = props.type === "multi" && searchQuery.length > 0 && filteredOptions.length > 0;

  return (
    <Command>
      <CommandInput
        ref={props.searchInputRef}
        placeholder="Search..."
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      {showSelectAllButton && (
        <SelectAllButton
          filteredOptions={filteredOptions}
          selected={props.selected}
          handleSelectAll={handleSelectAll}
        />
      )}
      <CommandList className={optionsListClassName}>
        <ContentFallback emptyText={props.emptyText}>
          <CommandGroup>
            {filteredOptions.map((option) => (
              <CommandItem
                key={option.value}
                onSelect={() => handleSelect(option.value)}
                className={cn("relative cursor-default pl-8", optionClassName)}
              >
                <span className="absolute left-2">{isSelected(option.value) && <CheckIcon className="h-4 w-4" />}</span>
                {option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </ContentFallback>
      </CommandList>
    </Command>
  );
}

type VirtualisedOptionsListProps<T extends string> = {
  options: ComboboxOption<T>[];
  emptyText?: string;
  setOpen: (open: boolean) => void;
  initialSearch: string;
  searchInputRef: React.Ref<HTMLInputElement>;
} & (SingleProps<T> | MultiProps<T>);

function VirtualisedOptionsList<T extends string>(props: VirtualisedOptionsListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState(props.initialSearch);
  const filteredOptions = props.options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 5,
  });

  const handleSelect = (value: T) => {
    if (props.type === "multi") {
      const newSelected = props.selected.includes(value)
        ? props.selected.filter((item) => item !== value)
        : [...props.selected, value];
      props.onChange(newSelected);
    } else {
      props.onChange(props.selected === value ? null : value);
      props.setOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (props.type === "multi") {
      const filteredValues = filteredOptions.map((opt) => opt.value);
      const newSelected = [...new Set([...props.selected, ...filteredValues])];
      props.onChange(newSelected);
    }
  };

  const isSelected = (value: T) => (props.type === "multi" ? props.selected.includes(value) : props.selected === value);

  const items = virtualizer.getVirtualItems();
  const showSelectAllButton = props.type === "multi" && searchQuery.length > 0 && filteredOptions.length > 0;

  return (
    <Command>
      <CommandInput
        ref={props.searchInputRef}
        placeholder="Search..."
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      {showSelectAllButton && (
        <SelectAllButton
          filteredOptions={filteredOptions}
          selected={props.selected}
          handleSelectAll={handleSelectAll}
        />
      )}
      <CommandList ref={parentRef} className={optionsListClassName}>
        <ContentFallback emptyText={props.emptyText}>
          <CommandGroup>
            <div
              className="w-full relative"
              style={{
                height: virtualizer.getTotalSize(),
              }}
            >
              <div
                className="absolute top-0 left-0 w-full"
                style={{
                  transform: `translateY(${items[0]?.start ?? 0}px)`,
                }}
              >
                {items.map((virtualItem) => {
                  const item = filteredOptions[virtualItem.index];
                  if (!item) return null;
                  return (
                    <CommandItem
                      key={virtualItem.key}
                      data-index={virtualItem.index}
                      ref={virtualizer.measureElement}
                      onSelect={() => handleSelect(item.value)}
                      value={item.label}
                      className={cn("relative cursor-default pl-8 whitespace-pre-line", optionClassName)}
                    >
                      <span className="absolute left-2">
                        {isSelected(item.value) && <CheckIcon className="h-4 w-4" />}
                      </span>
                      {item.label}
                    </CommandItem>
                  );
                })}
              </div>
            </div>
          </CommandGroup>
        </ContentFallback>
      </CommandList>
    </Command>
  );
}

type ComboboxRootProps<T extends string> = ComboboxProps<T> & {
  open: boolean;
  setOpen: (open: boolean) => void;
  onTriggerKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  onOpenAutoFocus?: (event: Event) => void;
  children: React.ReactNode;
};

export function ComboboxRoot<T extends string>({
  id,
  options,
  selected,
  name,
  open,
  setOpen,
  placeholder = "Select",
  disabled = false,
  type,
  onChange,
  children,
  className,
  isModal,
  inputRef,
  error,
  loading,
  onTriggerKeyDown,
  onOpenAutoFocus,
}: ComboboxRootProps<T>) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const composedTriggerRef = useMemo(() => composeRefs(triggerRef, inputRef), [inputRef]);
  const tabExitDirectionRef = useRef<"forward" | "backward" | null>(null);

  // A modal popover traps focus, so Tab would only cycle inside the list. Leave it the way a
  // native select does instead: close the list and carry on to the next (or back to this) field.
  const handleContentKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;

    e.preventDefault();
    tabExitDirectionRef.current = e.shiftKey ? "backward" : "forward";
    setOpen(false);
  };

  const handleCloseAutoFocus = (event: Event) => {
    const direction = tabExitDirectionRef.current;
    tabExitDirectionRef.current = null;

    const trigger = triggerRef.current;
    // Otherwise Radix returns focus to the trigger, which is also where Shift+Tab should land
    if (direction !== "forward" || !trigger) return;

    // Stay within an enclosing dialog, whose focus trap would reject anything outside it
    const next = getNextTabbable(trigger, trigger.closest("[role='dialog'],[role='alertdialog']") ?? document);
    if (!next) return;

    event.preventDefault();
    next.focus();
  };
  const selectedItems =
    type === "multi"
      ? selected.map((value) => options.find((opt) => opt.value === value)?.label).filter(Boolean)
      : selected
        ? [options.find((opt) => opt.value === selected)?.label].filter(Boolean)
        : [];

  const triggerText =
    type === "multi"
      ? selectedItems.length === 0
        ? placeholder
        : selectedItems.length <= 2
          ? selectedItems.join(", ")
          : `${selected.length} selected`
      : selected
        ? (options.find((opt) => opt.value === selected)?.label ?? placeholder)
        : placeholder;

  const isOptionSelected = type === "multi" ? selected.length === 0 : !selected;

  const showLoadingState = useSpinDelay(loading || false, { minDuration: 500 });
  const isMobile = useIsMobile();
  const portalContainer = usePortalContainer();
  const openedWithPointer = useRef<boolean>(false);

  const handleClear = () => {
    if (type === "multi") {
      onChange([]);
    } else {
      onChange(null);
    }
    // The clear button unmounts once the selection is empty, so move focus back to the trigger
    triggerRef.current?.focus();
  };

  const showClearButton = type === "multi" && selected.length > 0;

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen} modal={isModal}>
        <div className="relative">
          <PopoverTrigger asChild>
            <PopoverTriggerButton
              id={id}
              role="combobox"
              name={name}
              aria-expanded={open}
              disabled={disabled || !!error}
              className={cn(!!error ? "border-red-500" : "", className)}
              ref={composedTriggerRef}
              onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                openedWithPointer.current = event.detail > 0;
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>): void => {
                openedWithPointer.current = false;
                onTriggerKeyDown?.(event);
              }}
            >
              <span
                className={cn(
                  "truncate font-normal",
                  (isOptionSelected || showLoadingState) && "text-muted-foreground",
                )}
              >
                {!!error ? (
                  <div className="flex items-center justify-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <span className="text-foreground">{getErrorMessage(error)}</span>
                  </div>
                ) : showLoadingState ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading...</span>
                  </div>
                ) : (
                  triggerText
                )}
              </span>
              <div className="ml-2 flex items-center gap-2">
                {/* Reserves space for the clear button, which sits outside the trigger as nested buttons are invalid */}
                {showClearButton && <span className="size-4" aria-hidden="true" />}
                <ChevronDown
                  className={cn("h-4 w-4 shrink-0", (isOptionSelected || showLoadingState) && "text-muted-foreground")}
                />
              </div>
            </PopoverTriggerButton>
          </PopoverTrigger>
          {showClearButton && (
            <button
              type="button"
              aria-label="Clear selection"
              disabled={disabled || !!error}
              className="absolute right-9 top-1/2 -translate-y-1/2 rounded-full hover:bg-accent cursor-pointer"
              onClick={handleClear}
            >
              <X className="size-4 opacity-50 hover:opacity-100" />
            </button>
          )}
        </div>
        <PopoverContent
          container={portalContainer}
          className="w-[var(--radix-popover-trigger-width)] min-w-[220px] p-0"
          // Pointer opening avoids the mobile keyboard; keyboard activation retains search focus.
          onOpenAutoFocus={(event: Event): void => {
            if (isMobile && openedWithPointer.current) {
              event.preventDefault();
              return;
            }
            onOpenAutoFocus?.(event);
          }}
          onCloseAutoFocus={handleCloseAutoFocus}
          onKeyDown={handleContentKeyDown}
        >
          {children}
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Taller list and ~44px rows on small screens so options are easy to tap.
const optionsListClassName = "max-h-[200px] overflow-auto compact:max-h-[50vh]";
const optionClassName = "compact:py-3";

export function ContentFallback({
  children,
  emptyText = "No results found.",
}: {
  emptyText?: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <CommandEmpty>{emptyText}</CommandEmpty>
      {children}
    </>
  );
}

export function SelectAllButton<T extends string>({
  filteredOptions,
  selected,
  handleSelectAll,
}: {
  filteredOptions: ComboboxOption<T>[];
  selected: T[];
  handleSelectAll: () => void;
}) {
  const allFilteredSelected =
    filteredOptions.length > 0 && filteredOptions.every((opt) => selected.includes(opt.value));
  const isDisabled = filteredOptions.length === 0 || allFilteredSelected;

  return (
    <>
      <Button
        data-testid="select-all-button"
        type="button"
        variant={"ghost"}
        size={"sm"}
        disabled={isDisabled}
        className="m-1 text-left hover:bg-transparent"
        onClick={handleSelectAll}
      >
        Select All
      </Button>
      <hr className="-mx-1 h-px bg-gray-200" />
    </>
  );
}
